import { Box, Fab, Skeleton, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { Divider, Icons, Switch } from 'utoniq-ui'

import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

import { DeleteFavoriteMemberModal } from './components/DeleteFavoriteMemberModal'
import { DisableFavoriteMemberModal } from './components/DisableFavoriteMemberModal'
import { FavoriteMemberDragList } from './components/FavoriteMemberDragList'
import { MaxMembersReachedModal } from './components/MaxMembersReachedModal'

export type State = {
  artistId: string
  favoriteMembers: {
    id: string
    name: string
    avatar: string
    fanCount: number
  }[]
  enableFavoriteMember: boolean
  loading: boolean
  deletingMember: boolean
  changingSettings: boolean
  sortingMember: boolean
}

export type Action = {
  onMove: (prevIndex: number, nextIndex: number) => Promise<void>
  onDelete: (memberId: string) => Promise<void>
  onEnableSettings: () => Promise<void>
  onDisableSettings: () => Promise<void>
  onGoToCreate: () => void
  onGoToEdit: (favoriteMemberId: string) => void
  onGoToUsers: (favoriteMemberId: string) => void
}

export const ConsoleFavoriteMemberSettingsTemplate: Template<State, Action> = ({
  state,
  onMove,
  onDelete,
  onEnableSettings,
  onDisableSettings,
  onGoToCreate,
  onGoToEdit,
  onGoToUsers,
}) => {
  const { routerPages, router } = useRouter()
  const { t } = useTranslation('consoleFavoriteMemberSettings')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedMember, setSelectedMember] = useState<{
    id: string
    name: string
    avatar: string
    fanCount: number
  } | null>(null)
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [memberToDelete, setMemberToDelete] = useState<{
    id: string
    name: string
  } | null>(null)
  const [showMaxMembersModal, setShowMaxMembersModal] = useState(false)

  const handleToggleSettings = async () => {
    if (state.enableFavoriteMember) {
      // 推しメンバー機能を無効化する場合は確認モーダルを表示
      setShowDisableModal(true)
    } else {
      await onEnableSettings()
    }
  }

  const handleDisableSettings = async () => {
    await onDisableSettings()
    setShowDisableModal(false)
  }

  const handleDeleteMember = async (memberId: string) => {
    await onDelete(memberId)
    setShowDeleteModal(false)
    setMemberToDelete(null)
  }

  const handleShowDeleteModal = (member: {
    id: string
    name: string
    avatar: string
    fanCount: number
  }) => {
    setMemberToDelete(member)
    setShowDeleteModal(true)
  }

  const handleCreateMember = () => {
    if (state.favoriteMembers.length >= 50) {
      setShowMaxMembersModal(true)
    } else {
      onGoToCreate()
    }
  }

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleMembership,
          title: t('title'),
        }}
      >
        <Box sx={{ px: 5, pt: 5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1, pb: 3 }}>
              {state.loading && !state.sortingMember ? (
                <>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={32}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="80%" height={20} />
                </>
              ) : (
                <>
                  <Typography variant="h5">{t('settingsTitle')}</Typography>
                  {/* 説明文をタイトルの下に配置 */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      pt: 1,
                      display: 'block',
                      color: 'var(--color-Text-Secondary, #798A94)',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 300,
                      lineHeight: '20px',
                    }}
                  >
                    {t('description')}
                  </Typography>
                </>
              )}
            </Box>

            <Box sx={{ ml: 2, pt: 0.5 }}>
              {state.loading && !state.sortingMember ? (
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={24}
                  sx={{ borderRadius: 12 }}
                />
              ) : (
                <Switch
                  checked={state.enableFavoriteMember}
                  onChange={handleToggleSettings}
                  color="primary"
                  disabled={state.changingSettings}
                />
              )}
            </Box>
          </Box>
          <Divider sx={{ borderWidth: 4, borderColor: 'grey.50' }} />

          {/* 推しメンバー一覧 */}
          <FavoriteMemberDragList
            favoriteMembers={state.favoriteMembers}
            onMove={onMove}
            onGoToEdit={onGoToEdit}
            onGoToCreate={onGoToCreate}
            onDelete={onDelete}
            onGoToUsers={onGoToUsers}
            onShowDeleteModal={handleShowDeleteModal}
            loading={state.loading}
            sortingMember={state.sortingMember}
          />
        </Box>

        {/* 右下のプラスアイコン */}
        <Stack direction="row-reverse">
          <Stack
            sx={{
              position: 'fixed',
              bottom: 16,
              zIndex: 100,
              maxWidth: 600,
              margin: '0 auto',
              px: 4,
              pb: 0,
            }}
          >
            <Fab size="medium" color="primary" onClick={handleCreateMember}>
              <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
            </Fab>
          </Stack>
        </Stack>
      </ConsoleBaseLayout>

      {/* 推しメンバー機能無効化確認モーダル */}
      <DisableFavoriteMemberModal
        open={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onDisable={handleDisableSettings}
      />

      {/* 推しメンバー削除確認モーダル */}
      <DeleteFavoriteMemberModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setMemberToDelete(null)
        }}
        onDelete={handleDeleteMember}
        memberId={memberToDelete?.id ?? ''}
        memberName={memberToDelete?.name ?? ''}
      />

      {/* 最大メンバー数到達モーダル */}
      <MaxMembersReachedModal
        open={showMaxMembersModal}
        onClose={() => setShowMaxMembersModal(false)}
      />
    </ArtistThemeProvider>
  )
}
